import { CmpMemberSectionEnum } from "../../../../graphql/types";
import type { UpdateMemberFormState } from "./UpdateMember";
import { hasUnsavedMemberFormChanges } from "../memberFormDirtyState";
import { normalizeMemberLoginEmail } from "./memberCredentialActions";
import { UserFacingError } from "../../../../utils/userFacingError";

export type MemberContactSaveTarget =
  | { role: keyof UpdateMemberFormState["contacts"] }
  | { customContactId: string };

export const memberSectionErrorKey = (section: CmpMemberSectionEnum, target?: MemberContactSaveTarget) =>
  target ? "role" in target ? `contact:${target.role}` : `custom:${target.customContactId}` : section;

export const memberSectionSaveOrder = [
  CmpMemberSectionEnum.MembershipTracking,
  CmpMemberSectionEnum.MemberDirectory,
  CmpMemberSectionEnum.MemberAddress,
  CmpMemberSectionEnum.SocialLinks,
  CmpMemberSectionEnum.Contacts,
  CmpMemberSectionEnum.BillingAddress,
  CmpMemberSectionEnum.CustomContacts,
  CmpMemberSectionEnum.MemberInsights,
  CmpMemberSectionEnum.ReferralTracking,
  CmpMemberSectionEnum.AssociateMember,
  CmpMemberSectionEnum.AffiliatedChamber,
  CmpMemberSectionEnum.AffinityPrograms,
  CmpMemberSectionEnum.SeoTags,
] as const;

type RevisionedMemberDetail = {
  member: { id: string; updatedAtUtcMilli?: number | null; adminConsoleLoginEmail?: string | null };
};

/** An eventual read must never replace a newer committed mutation result. */
export const isCurrentMemberReadback = (
  committed: RevisionedMemberDetail,
  readback: RevisionedMemberDetail | null | undefined,
) => !!readback && readback.member.id === committed.member.id &&
  (readback.member.updatedAtUtcMilli ?? 0) >= (committed.member.updatedAtUtcMilli ?? 0) &&
  normalizeMemberLoginEmail(readback.member.adminConsoleLoginEmail) ===
    normalizeMemberLoginEmail(committed.member.adminConsoleLoginEmail);

export const confirmMemberIdentityRevision = ({
  detail, memberId, email, previousRevision,
}: {
  detail: RevisionedMemberDetail | null | undefined;
  memberId: string;
  email: string;
  previousRevision: number | null;
}): number => {
  const revision = detail?.member.updatedAtUtcMilli;
  if (!detail || detail.member.id !== memberId ||
    normalizeMemberLoginEmail(detail.member.adminConsoleLoginEmail) !== normalizeMemberLoginEmail(email) ||
    typeof revision !== "number" || !Number.isFinite(revision) || revision <= (previousRevision ?? 0)) {
    throw new UserFacingError("The sign-in email was changed, but the page did not update. Refresh the page before you make more changes.");
  }
  return revision;
};

const sectionKeys: Record<CmpMemberSectionEnum, (keyof UpdateMemberFormState)[]> = {
  [CmpMemberSectionEnum.MembershipTracking]: ["administrative"],
  [CmpMemberSectionEnum.MemberDirectory]: ["directory", "logo"],
  [CmpMemberSectionEnum.MemberAddress]: ["address"],
  [CmpMemberSectionEnum.SocialLinks]: ["socialLinks"],
  [CmpMemberSectionEnum.Contacts]: ["contacts", "contactSameAs"],
  [CmpMemberSectionEnum.BillingAddress]: ["billingAddress"],
  [CmpMemberSectionEnum.CustomContacts]: ["customContacts"],
  [CmpMemberSectionEnum.MemberInsights]: ["companyDetails", "insights"],
  [CmpMemberSectionEnum.ReferralTracking]: ["referralTracking"],
  [CmpMemberSectionEnum.AssociateMember]: ["association"],
  [CmpMemberSectionEnum.AffiliatedChamber]: ["affiliatedChamberMember"],
  [CmpMemberSectionEnum.AffinityPrograms]: ["affinityProgramEnrollments"],
  [CmpMemberSectionEnum.SeoTags]: [],
};

/** The section boundary is also used for reset, dirty checks, and save readback. */
export const mergeMemberSection = (
  baseline: UpdateMemberFormState,
  source: UpdateMemberFormState,
  section: CmpMemberSectionEnum,
  target?: MemberContactSaveTarget,
): UpdateMemberFormState => {
  if (section === CmpMemberSectionEnum.Contacts && target && "role" in target) {
    return {
      ...baseline,
      contacts: { ...baseline.contacts, [target.role]: source.contacts[target.role] },
      contactSameAs: { ...baseline.contactSameAs, [target.role]: source.contactSameAs[target.role] },
    };
  }
  if (section === CmpMemberSectionEnum.CustomContacts && target && "customContactId" in target) {
    const contact = source.customContacts.find(item => item.id === target.customContactId);
    const exists = baseline.customContacts.some(item => item.id === target.customContactId);
    return { ...baseline, customContacts: exists
      ? baseline.customContacts.flatMap(item => item.id === target.customContactId ? contact ? [contact] : [] : [item])
      : contact ? [...baseline.customContacts, contact] : baseline.customContacts };
  }
  if (section === CmpMemberSectionEnum.SeoTags) {
    return { ...baseline, directory: { ...baseline.directory, seoTags: source.directory.seoTags } };
  }
  const next = { ...baseline };
  for (const key of sectionKeys[section]) {
    Object.assign(next, { [key]: source[key] });
  }
  if (section === CmpMemberSectionEnum.MemberDirectory) {
    next.directory = { ...source.directory, seoTags: baseline.directory.seoTags };
    // Numbered Company shows under Member Name (IF11 MB3) but is stored in companyDetails.
    next.companyDetails = { ...baseline.companyDetails, numberedCompany: source.companyDetails.numberedCompany };
  }
  if (section === CmpMemberSectionEnum.MemberInsights) {
    next.companyDetails = { ...source.companyDetails, numberedCompany: baseline.companyDetails.numberedCompany };
    next.membership = {
      ...baseline.membership,
      emailCommunicationConsentConfirmed: source.membership.emailCommunicationConsentConfirmed,
    };
  }
  return next;
};

export const isMemberSectionDirty = (
  baseline: UpdateMemberFormState,
  current: UpdateMemberFormState,
  section: CmpMemberSectionEnum,
  target?: MemberContactSaveTarget,
) => hasUnsavedMemberFormChanges({ baseline, current: mergeMemberSection(baseline, current, section, target) });

/** Keep edits made during a request, plus every field outside the saved section. */
export const applySavedMemberSection = (
  current: UpdateMemberFormState,
  submitted: UpdateMemberFormState,
  persisted: UpdateMemberFormState,
  section: CmpMemberSectionEnum
) => isMemberSectionDirty(submitted, current, section)
  ? current
  : mergeMemberSection(current, persisted, section);

/** A section save advances only that card's baseline, including local row IDs. */
export const applySavedMemberForm = (
  state: { current: UpdateMemberFormState; savedBaseline: UpdateMemberFormState },
  submitted: UpdateMemberFormState,
  persisted: UpdateMemberFormState,
  section?: CmpMemberSectionEnum,
  target?: MemberContactSaveTarget,
) => {
  let current = state.current;
  if (section && target) {
    const customId = "customContactId" in target ? target.customContactId : undefined;
    // Add drafts are held outside the saved list. A confirmed delete removes
    // the selected record even if it had edits when Delete was requested.
    const added = customId !== undefined &&
      !state.savedBaseline.customContacts.some(contact => contact.id === customId) &&
      !current.customContacts.some(contact => contact.id === customId);
    const deleted = customId !== undefined &&
      !submitted.customContacts.some(contact => contact.id === customId);
    if (added || deleted || !isMemberSectionDirty(submitted, current, section, target)) {
      current = mergeMemberSection(current, persisted, section, target);
    }
  } else if (section) {
    current = applySavedMemberSection(current, submitted, persisted, section);
  } else if (!hasUnsavedMemberFormChanges({ baseline: submitted, current })) {
    current = persisted;
  }
  return {
    current,
    savedBaseline: section
      ? mergeMemberSection(state.savedBaseline, persisted, section, target)
      : persisted,
  };
};
